import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useCopyToClipboard } from 'react-use'

import CopyIcon from '@/assets/icons/copy.svg?react'
import { Divider, EmptyView, message, Trans } from '@/components'
import { portProtocolLabel } from '@/consts/machine'
import { Instance, Port } from '@/models/instance'
import { cn } from '@/utils/style'

interface TeamInstanceAccessProps {
  instance: Instance
}

const getHostFromLink = (link: string) => {
  return link.replace(/^(.*:\/\/)/, '')
}

const getSSHCommand = (link: string, user: string) => {
  const hostPort = getHostFromLink(link).split(':')
  const ip = hostPort[0]
  const port = hostPort[1]
  return `ssh -p ${port} ${user}@${ip}`
}

function SSHPortDisplay({ port, instance }: { port: Port; instance: Instance }) {
  const [state, copyToClipboard] = useCopyToClipboard()
  const sshPort = getHostFromLink(port.url).split(':')[1]
  const auth = instance.auth
  const { t } = useTranslation()

  useEffect(() => {
    if (state.value) {
      message.success(t('instance.access.message.copied'))
    }
  }, [state, t])

  if (!auth) {
    return null
  }

  return (
    <div className="bg-panel-gray-100 p-4 rounded-lg">
      <div className="grid grid-cols-3 gap-4">
        {/* SSH Command */}
        <div className="flex flex-col gap-1">
          <div className="t-xs text-secondary">
            <Trans i18nKey="instance.detail.access.sshCommand">SSH Command</Trans>
          </div>
          <div className="t-sb flex items-center gap-1">
            <span className="truncate">{getSSHCommand(port.url, auth.user)}</span>
            <CopyIcon
              className={cn(
                'w-4 h-4 text-highlight flex-shrink-0',
                port.url ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
              )}
              onClick={() => {
                if (port.url) {
                  copyToClipboard(getSSHCommand(port.url, auth.user))
                }
              }}
            />
          </div>
        </div>

        {/* Service Port */}
        <div className="flex flex-col gap-1">
          <div className="t-xs text-secondary">
            <Trans i18nKey="instance.detail.access.servicePort">Service Port</Trans>
          </div>
          <div className="t-sb flex items-center gap-1">
            {sshPort}
            <CopyIcon
              className={cn(
                'w-4 h-4 text-highlight',
                sshPort ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
              )}
              onClick={() => {
                if (sshPort) {
                  copyToClipboard(sshPort)
                }
              }}
            />
          </div>
        </div>

        {/* Empty for 3-column layout */}
        <div />

        {/* User */}
        <div className="flex flex-col gap-1">
          <div className="t-xs text-secondary">
            <Trans i18nKey="instance.detail.access.user">User</Trans>
          </div>
          <div className="t-sb flex items-center gap-1">
            {auth.user}
            <CopyIcon
              className={cn(
                'w-4 h-4 text-highlight',
                auth.user ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
              )}
              onClick={() => {
                if (auth.user) {
                  copyToClipboard(auth.user)
                }
              }}
            />
          </div>
        </div>

        {/* Password */}
        <div className="flex flex-col gap-1">
          <div className="t-xs text-secondary">
            <Trans i18nKey="instance.detail.access.password">Password</Trans>
          </div>
          <div className="t-sb flex items-center gap-1">
            ********
            <CopyIcon
              className={cn(
                'w-4 h-4 text-highlight',
                auth.password ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
              )}
              onClick={() => {
                if (auth.password) {
                  copyToClipboard(auth.password)
                }
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function OtherPortDisplay({ port }: { port: Port }) {
  const [state, copyToClipboard] = useCopyToClipboard()
  const { t } = useTranslation()

  useEffect(() => {
    if (state.value) {
      message.success(t('instance.access.message.copied'))
    }
  }, [state, t])

  return (
    <div className="bg-panel-gray-100 p-4 rounded-lg">
      <div className="grid grid-cols-3 gap-4">
        {/* Protocol */}
        <div className="flex flex-col gap-1">
          <div className="t-xs text-secondary">
            <Trans i18nKey="instance.detail.access.protocol">Protocol</Trans>
          </div>
          <div className="t-sb">{portProtocolLabel[port.protocol]}</div>
        </div>

        {/* Hostname */}
        <div className="flex flex-col gap-1">
          <div className="t-xs text-secondary">
            <Trans i18nKey="instance.detail.access.hostname">Hostname</Trans>
          </div>
          <div className="t-sb flex items-center gap-1">
            <span className="truncate">{getHostFromLink(port.url).split(':')[0]}</span>
            <CopyIcon
              className={cn(
                'w-4 h-4 text-highlight flex-shrink-0',
                port.url ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
              )}
              onClick={() => {
                if (port.url) {
                  copyToClipboard(getHostFromLink(port.url).split(':')[0])
                }
              }}
            />
          </div>
        </div>

        {/* Port */}
        <div className="flex flex-col gap-1">
          <div className="t-xs text-secondary">
            <Trans i18nKey="instance.detail.access.port">Port</Trans>
          </div>
          <div className="t-sb flex items-center gap-1">
            {getHostFromLink(port.url).split(':')[1]}
            <CopyIcon
              className={cn(
                'w-4 h-4 text-highlight',
                port.url ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed',
              )}
              onClick={() => {
                if (port.url) {
                  copyToClipboard(getHostFromLink(port.url).split(':')[1])
                }
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export function TeamInstanceAccess({ instance }: TeamInstanceAccessProps) {
  const ports = instance.ports || []

  if (ports.length === 0) {
    return (
      <div className="mt-4">
        <EmptyView
          title={
            <span className="t-sm text-secondary">
              <Trans i18nKey="instance.detail.access.noPorts">No ports available</Trans>
            </span>
          }
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {ports.map((port, index) => (
        <div key={index}>
          <div className="flex gap-2 items-center px-0 py-2 mb-4">
            <div className="bg-btn-secondary h-5 w-1" />
            <p className="h6 text-primary">
              {port.srcPort === 22 ? (
                <Trans i18nKey="instance.detail.access.sshAccess">Access</Trans>
              ) : (
                <>
                  {portProtocolLabel[port.protocol]} (Port {port.srcPort})
                </>
              )}
            </p>
          </div>

          {port.srcPort === 22 ? (
            <SSHPortDisplay port={port} instance={instance} />
          ) : (
            <OtherPortDisplay port={port} />
          )}

          {index < ports.length - 1 && <Divider className="my-6" />}
        </div>
      ))}
    </div>
  )
}
